// Dev-only sample slots so the Plan views can be exercised without a live
// Notion token. Enabled by ?demo=1 (see PlanList). NOT used in production —
// both publish paths hard-bail in demo mode. Dates are relative to today so
// the demo stays evergreen.

import type { GuideRef, PlanSlot } from "@/lib/load";
import { lookupGuideByAnyName } from "@/config/guides";
import { guidePageIdByPeekName } from "@/config/notion";
import { azTodayISO, plusDaysISO } from "@/lib/dates";

function ref(peekName: string): GuideRef {
  const g = lookupGuideByAnyName(peekName);
  if (!g) throw new Error(`demo: unknown guide ${peekName}`);
  return {
    id: g.id,
    peekName: g.peekName,
    availName: g.availName,
    region: g.region,
    pageId: guidePageIdByPeekName(g.peekName) ?? null,
  };
}

interface Seed {
  dayOffset: number; // days from today
  hhmm: string;
  tour: string;
  market: PlanSlot["market"];
  guests: number;
  capacity: number;
  status: string[];
  avail: string[];
  unavail?: string[];
  assigned?: string[];
}

// Tour "TUC Haunted History" + market "TUS" is intentional — it mirrors real
// PlanSlot shape (Notion stores the TUC-prefixed tour label; load.ts
// canonicalizes the market to TUS). Seeds exercise every availability state:
// ✨ picks, ❌ blocks (Mike on the day-2 R66), a no-✨-but-coverable slot
// (day-2 PSCT), and a true gap (day-2 TUS — every Tucson guide marked ❌).
const SEEDS: Seed[] = [
  { dayOffset: 1, hhmm: "18:00", tour: "FLG Route 66", market: "FLG", guests: 6, capacity: 28, status: ["BOOKABLE"], avail: ["Mike Miller", "Steve Benning"] },
  { dayOffset: 1, hhmm: "19:00", tour: "FLG Haunted History", market: "FLG", guests: 12, capacity: 28, status: ["BOOKABLE"], avail: ["James Hasapis", "Sean Golightly"], assigned: ["Mike Miller"] },
  { dayOffset: 1, hhmm: "19:00", tour: "PSCT Haunted History", market: "PSCT", guests: 4, capacity: 20, status: ["GUIDE COVERAGE REQUESTED"], avail: ["Shane Harding", "Meg Mezeske"] },
  { dayOffset: 1, hhmm: "20:00", tour: "TUC Haunted History", market: "TUS", guests: 0, capacity: 25, status: ["BOOKABLE"], avail: ["Devon Solovey"] },
  { dayOffset: 2, hhmm: "18:00", tour: "FLG Route 66", market: "FLG", guests: 22, capacity: 28, status: ["BOOKABLE"], avail: ["Steve Benning"], unavail: ["Mike Miller"] },
  { dayOffset: 2, hhmm: "19:00", tour: "PSCT Haunted History", market: "PSCT", guests: 2, capacity: 20, status: [], avail: [] },
  { dayOffset: 2, hhmm: "20:00", tour: "TUC Haunted History", market: "TUS", guests: 5, capacity: 25, status: ["BOOKABLE"], avail: [], unavail: ["Devon Solovey", "Jade Marmorstein", "Matthew Gore"] },
];

export function demoSlots(): PlanSlot[] {
  const today = azTodayISO();
  return SEEDS.map((s, i) => {
    const date = plusDaysISO(today, s.dayOffset);
    return {
      pageId: `demo-${i}`,
      peekTimeslotId: `demo-ts-${i}`,
      slotKey: `${s.market}|${date}|${s.hhmm}|DEMO`,
      title: `${date} ${s.hhmm} — ${s.tour}`,
      date,
      hhmm: s.hhmm,
      startLabel: labelOf(s.hhmm),
      startIso: `${date}T${s.hhmm}:00.000-07:00`,
      tour: s.tour,
      market: s.market,
      guests: s.guests,
      capacity: s.capacity,
      spotsLeft: Math.max(s.capacity - s.guests, 0),
      status: s.status,
      assignedGuideNames: s.assigned ?? [],
      assignedGuidePeekId: "",
      available: s.avail.map(ref),
      unavailable: (s.unavail ?? []).map(ref),
      excluded: false,
    };
  });
}

function labelOf(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
}
